import { useUIColors } from '@/core/hooks';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import { Animated, Platform, StyleSheet, View } from 'react-native';

/** Match SelectionChip time sizes so shimmer height = real slot grid. */
const DESKTOP_CHIP_HEIGHT = 52;
const MOBILE_CHIP_HEIGHT = 48;
const CHIP_GAP = 12;
/** Desktop: 6 chips/row × 2 rows. Mobile: 3 chips/row × 2 rows. */
const DESKTOP_SHIMMER_COUNT = 12;
const MOBILE_SHIMMER_COUNT = 6;
const CHIP_RADIUS = 10;
const SHIMMER_DURATION = 1600;

export const TIME_SLOTS_AREA_MIN_HEIGHT_DESKTOP =
  DESKTOP_CHIP_HEIGHT * 2 + CHIP_GAP;
export const TIME_SLOTS_AREA_MIN_HEIGHT_MOBILE =
  MOBILE_CHIP_HEIGHT * 2 + CHIP_GAP;

export interface AppointmentTimeSlotsSkeletonProps {
  isMobile?: boolean;
}

interface SlotShimmerProps {
  height: number;
  baseColor: string;
  highlightColor: string;
  progress: Animated.Value;
}

/**
 * Local slot shimmer. Shared Skeleton uses native-driver loop, which often
 * plays once on web. JS driver keeps the sweep repeating while slots load.
 */
function SlotShimmer({
  height,
  baseColor,
  highlightColor,
  progress,
}: Readonly<SlotShimmerProps>) {
  const [chipWidth, setChipWidth] = useState(0);
  const travel = Math.max(chipWidth, 1);

  const translateX = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [-travel, travel],
  });

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setChipWidth(event.nativeEvent.layout.width);
  }, []);

  return (
    <View
      style={[styles.shimmerCard, { height, backgroundColor: baseColor }]}
      onLayout={handleLayout}
    >
      <Animated.View
        style={[
          styles.shimmerOverlay,
          { transform: [{ translateX }] },
        ]}
      >
        <LinearGradient
          colors={[
            baseColor,
            baseColor,
            highlightColor,
            highlightColor,
            baseColor,
            baseColor,
          ]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          locations={[0, 0.3, 0.45, 0.55, 0.7, 1]}
          style={styles.shimmerGradient}
        />
      </Animated.View>
    </View>
  );
}

/**
 * Small shimmer cards matching time-slot chip layout (web book-appointment).
 */
export function AppointmentTimeSlotsSkeleton({
  isMobile = false,
}: Readonly<AppointmentTimeSlotsSkeletonProps>) {
  const colors = useUIColors();
  const shimmerProgress = useRef(new Animated.Value(0)).current;
  const count = isMobile ? MOBILE_SHIMMER_COUNT : DESKTOP_SHIMMER_COUNT;
  const height = isMobile ? MOBILE_CHIP_HEIGHT : DESKTOP_CHIP_HEIGHT;
  const baseColor = isMobile
    ? colors.backgroundLevel3
    : colors.backgroundLevel2;
  const highlightColor = isMobile
    ? colors.backgroundLevel1
    : colors.backgroundLevel4;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.timing(shimmerProgress, {
        toValue: 1,
        duration: SHIMMER_DURATION,
        useNativeDriver: Platform.OS !== 'web',
      }),
    );
    animation.start();
    return () => {
      animation.stop();
      shimmerProgress.setValue(0);
    };
  }, [shimmerProgress]);

  return (
    <View style={[styles.grid, isMobile && styles.gridMobile]}>
      {Array.from({ length: count }, (_, index) => `slot-shimmer-${index + 1}`)
        .map((skeletonKey) => (
        <View
          key={skeletonKey}
          style={isMobile ? styles.chipWrapMobile : styles.chipWrap}
        >
          <SlotShimmer
            height={height}
            baseColor={baseColor}
            highlightColor={highlightColor}
            progress={shimmerProgress}
          />
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: CHIP_GAP,
    width: '100%',
  },
  gridMobile: {
    gap: CHIP_GAP,
    justifyContent: 'flex-start',
  },
  chipWrap: {
    width: 97,
  },
  chipWrapMobile: {
    width: '31%',
    flexGrow: 0,
    flexShrink: 0,
  },
  shimmerCard: {
    width: '100%',
    borderRadius: CHIP_RADIUS,
    overflow: 'hidden',
    position: 'relative',
  },
  shimmerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: '200%',
    opacity: 0.85,
  },
  shimmerGradient: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
});

export default AppointmentTimeSlotsSkeleton;
